import React, { useEffect, useRef, createElement } from 'react';
import { motion } from 'framer-motion';
import { DottedSurface } from './ui/DottedSurface';
declare global {
  interface Window {
    paypal?: any;
  }
}
export function PricingSection() {
  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const tiers = [{
    name: 'Starter',
    monthly: '£97',
    description: 'Website in a Day',
    accentBg: 'bg-orange-900/30',
    accentText: 'text-orange-100',
    planId: 'P-8BK506265R3975300NGX3OKY',
    features: ['Free Website', 'On-Page SEO', 'SSL & Fast Loading', 'Lead Capture System']
  }, {
    name: 'Unlimited',
    monthly: '£297',
    description: 'All-In-One Growth Package',
    accentBg: 'gradient-animated',
    accentText: 'text-white',
    highlight: true,
    planId: 'P-4H292922XM117581JNGX3ONI',
    features: ['Everything in Starter', 'CRM + Lead Tracking', 'SMS, Email & Review Automations', 'Google 5 Star Reviews Automation', '24/7 Live AI ChatBot']
  }, {
    name: 'Pro',
    monthly: '£497',
    description: 'Full Automation Suite',
    accentBg: 'bg-amber-900/30',
    accentText: 'text-amber-100',
    planId: 'P-86U07606WP469330CNGX3OPQ',
    features: ['Everything in Unlimited', '24/7 Live AI Receptionist', 'Automatic Appointments Booking Automation']
  }];
  const paypalRendered = useRef<Set<string>>(new Set());
  useEffect(() => {
    const renderButtons = () => {
      if (!window.paypal) return;
      tiers.forEach(tier => {
        const containerId = `paypal-card-container-${tier.planId}`;
        const container = document.getElementById(containerId);
        if (container && !paypalRendered.current.has(tier.planId)) {
          paypalRendered.current.add(tier.planId);
          try {
            window.paypal.Buttons({
              fundingSource: window.paypal.FUNDING.CARD,
              style: {
                shape: 'rect',
                color: 'black',
                layout: 'vertical',
                label: 'subscribe'
              },
              createSubscription: function (data: any, actions: any) {
                return actions.subscription.create({
                  plan_id: tier.planId
                });
              },
              onApprove: function (data: any) {
                alert(data.subscriptionID);
              }
            }).render(`#${containerId}`);
          } catch (error) {
            console.error(`PayPal button render failed for ${tier.name}:`, error);
          }
        }
      });
    };
    const existingScript = document.querySelector('script[src*="paypal.com/sdk/js"]');
    if (existingScript) {
      if (window.paypal) {
        // Delay to ensure animated containers are in the DOM
        setTimeout(renderButtons, 500);
      } else {
        existingScript.addEventListener('load', () => {
          setTimeout(renderButtons, 500);
        });
      }
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://www.paypal.com/sdk/js?client-id=AdKnolWDa1eIyawCNqi6l_bFue-F0VrZJQcrKDS0EXrdrqTGhecsBSCy_Ea5R_AD8Zk1JP2esvUWfI5a&vault=true&intent=subscription';
    script.setAttribute('data-sdk-integration-source', 'button-factory');
    script.async = true;
    script.onload = () => {
      // Delay to ensure Framer Motion animated containers are visible in the DOM
      setTimeout(renderButtons, 500);
    };
    document.body.appendChild(script);
  }, []);
  return <section id="pricing" className="py-16 md:py-24 gradient-section-dark relative overflow-hidden" data-id="element-303">
      <DottedSurface className="absolute inset-0 z-0" aria-hidden="true" data-id="element-304" />
      <div className="container mx-auto px-4 relative z-10" data-id="element-305">
        <div className="text-center mb-10 md:mb-16" data-id="element-306">
          <h2 className="text-4xl md:text-5xl font-black text-white mb-3 md:mb-4" data-id="element-307">
            Investment
          </h2>
          <p className="text-lg md:text-xl text-gray-400" data-id="element-308">
            Transparent Pricing. No Hidden Fees. Cancel Anytime.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 md:gap-6 max-w-6xl mx-auto mb-10 md:mb-16 items-start" data-id="element-309">
          {tiers.map((tier, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: index * 0.15,
          ease: 'easeOut'
        }} viewport={{
          once: true
        }} className={`bg-[#1A1A24] rounded-2xl md:rounded-3xl p-5 md:p-8 flex flex-col ${tier.highlight ? 'border border-primary shadow-[0_0_30px_rgba(232,76,26,0.15)] md:-translate-y-4 bg-gradient-to-b from-[#2A1A10] to-[#1A1A24]' : 'border border-gray-800'}`} data-id="element-310">
              {tier.highlight && <div className="text-center mb-3 md:mb-4 -mt-1 md:-mt-2" data-id="element-311">
                  <span className="gradient-animated text-white text-[10px] md:text-xs font-bold uppercase tracking-wider py-1 md:py-1.5 px-3 md:px-4 rounded-full inline-block" data-id="element-312">
                    Most Popular
                  </span>
                </div>}
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 md:mb-6 text-center" data-id="element-313">
                {tier.name}
              </h3>

              <div className={`rounded-xl md:rounded-2xl p-3 md:p-4 mb-6 md:mb-8 text-center ${tier.accentBg}`} data-id="element-314">
                <div className="flex items-baseline justify-center gap-1 md:gap-2 mb-1" data-id="element-315">
                  <span className={`text-2xl md:text-4xl font-black ${tier.accentText}`} data-id="element-316">
                    {tier.monthly}
                  </span>
                  <span className={`text-base md:text-lg font-medium ${tier.accentText}`} data-id="element-317">
                    /Month
                  </span>
                </div>
                <div className={`text-xs md:text-sm font-medium ${tier.accentText} opacity-80`} data-id="element-318">
                  {tier.description}
                </div>
                <div className="text-[10px] md:text-xs font-semibold text-green-400 mt-1" data-id="element-319">
                  No Setup Cost
                </div>
              </div>

              <ul className="space-y-2 md:space-y-3 mb-6 md:mb-8 flex-grow" data-id="element-320">
                {tier.features.map((feature, i) => <li key={i} className="flex items-start space-x-2 md:space-x-3" data-id="element-321">
                    <div className="mt-1.5 md:mt-1 w-1.5 h-1.5 rounded-full bg-white flex-shrink-0" data-id="element-322" />
                    <span className="text-gray-300 text-xs md:text-sm font-medium" data-id="element-323">
                      {feature}
                    </span>
                  </li>)}
              </ul>

              <div id={`paypal-card-container-${tier.planId}`} className="mt-auto w-full min-h-[55px] overflow-hidden rounded-lg" data-id="element-324" />
            </motion.div>)}
        </div>
      </div>
    </section>;
}